import { MapScenarioFilterV1 } from 'src/components/dispatch/unassignedFilters/types';
import { hasRotationNarrowing, RotationFilterValue, VehicleFilterMode } from 'src/components/dispatch/vehicleFilter';
import { PlanningConditions, PlanningSettingRequest } from 'src/entities/PlanningHistory.entity';
import { PlanningsGroupEntity } from 'src/entities/PlanningsGroup.entity';
import { SelectedCycle } from 'src/entities/SelectedCycle.entity';
import { TruckDisplayStatusVo } from 'src/vo/TruckDisplayStatusVo';

/** id=0 の合成グループ。V3Plans/Component.tsx:372-376 が作るものと同じ形 */
export const ALL_TRUCKS_GROUP_ID = 0;
export const ALL_TRUCKS_GROUP_NAME = 'すべて表示する';

// buildPlanningSetting.ts と同じ理由で、参照するフィールドだけの最小形で受け取る
// （画面表示用の重量級 entity を要求すると呼び出し側・テスト双方の fixture が肥大化する）。
export type MapPlanningSettingDeliveryLike = {
  id: number;
  truckId: number | null;
  driverId: number | null;
};

export type MapPlanningSettingTruckLike = {
  id: number;
  licensePlateValue: string;
};

export type MapPlanningSettingDriverLike = {
  id: number;
  name: string;
};

export type BuildMapPlanningSettingParams = {
  /** Presenter の `truckDisplayStatus` state。/map 固有の値を含むので型は VehicleFilterMode */
  vehicleFilterMode: VehicleFilterMode;
  /** `allRowsSelected(selectableRowIds, selectedRowIds)` の結果 */
  allDeliverySelected: boolean;
  /** Presenter の `rotations` state。履歴に持てない絞り込みなので allDeliverySelected の判定に使う */
  rotations: readonly RotationFilterValue[];
  /** Presenter の `constraintViolationOnly` state。同じく allDeliverySelected の判定に使う */
  constraintViolationOnly: boolean;
  /** `buildSelectedCycleIndexes` の結果。ここで回転を計算し直さない */
  selectedCycleIndexes: readonly SelectedCycle[];
  deliveries: readonly MapPlanningSettingDeliveryLike[];
  trucks: readonly MapPlanningSettingTruckLike[];
  drivers: readonly MapPlanningSettingDriverLike[];
  /**
   * 車両グループで絞り込んでいるときに、選ばれているグループ。
   * 絞り込んでいなければ空（= ID 0 の「すべて表示する」を送る）。
   */
  selectedTruckGroups?: readonly PlanningsGroupEntity[];
  scenarioFilter: MapScenarioFilterV1;
  /** PlanningPresenter がまだ通知していない間だけ null */
  planningConditions: PlanningConditions | null;
};

/**
 * /map の車両リスト絞り込み（7 値）を、履歴が持てる `TruckDisplayStatusVo`（4 値）へ写す。
 *
 * Why not TruckDisplayStatusVo を 7 値へ広げない: vehicleFilter.ts が明記しているとおり、
 *   この VO は PlansContext / V3Plans / V3Timeline / PlanningHistory でも分岐に使われており、
 *   値を足すと関係の無い画面の分岐に漏れる。
 * Why not 回転絞り込みを別フィールドで持たない: どの回転を実行対象にしたかは
 *   `selectedCycleIndexes` が既に持っており、二重に持たせると食い違いうる。API 側の
 *   `select_shift_by_truck_display_status`（planning_scenario_item.rb）も
 *   配車済み/未配車の 2 値以外は shift を絞らないため、'すべて表示' と挙動が一致する。
 */
/**
 * 「全車両を対象にした」として保存して良いかを判定する。
 *
 * API (`planning_scenario_item#select_shift_by_selection`) は `allDeliverySelected` が true だと
 * 個別選択を無視して当日の全勤務を採る。画面の「全選択」は絞り込み後の表示行が基準なので、
 * 履歴に持てない絞り込み（回転・制約違反のみ・選択中のみ）が掛かったまま true で保存すると、
 * 再実行のときだけ非表示だった勤務まで対象に広がる。
 *
 * Why not 配車済み / 未配車を false にしない: この 2 つは truckDisplayStatus として保存され、
 * API 側の select_shift_by_truck_display_status が再実行時点の配車状況で再評価する。
 */
export function resolveAllDeliverySelected(params: {
  allSelected: boolean;
  vehicleFilterMode: VehicleFilterMode;
  rotations: readonly RotationFilterValue[];
  constraintViolationOnly: boolean;
}): boolean {
  const { allSelected, vehicleFilterMode, rotations, constraintViolationOnly } = params;
  if (!allSelected) return false;
  if (constraintViolationOnly) return false;
  if (hasRotationNarrowing(rotations)) return false;
  return vehicleFilterMode !== '選択中のみ表示する';
}

export function toPlanningSettingTruckDisplayStatus(mode: VehicleFilterMode): TruckDisplayStatusVo {
  switch (mode) {
    case '選択中のみ表示する':
    case '配車済み車両のみ表示':
    case '未配車の車両のみ表示':
      return mode;
    default:
      return 'すべて表示';
  }
}

/**
 * /map の画面状態から、シナリオ履歴に保存する `planningSetting` を組み立てる純粋関数。
 *
 * `deliveryConditions` の 6 フィールドを必ず埋める。/map は以前これを空オブジェクトで
 * 送っており、その履歴からシナリオを作ると `selectedGroupIds.map` が nil に対して
 * 呼ばれて job が error になっていた（planning_scenario_item.rb:48）。
 *
 * Why not 解決できない要素を null で埋めない: API は `selected_deliveries.pluck('truckId')`
 *   で truckId を引くため、null 要素が 1 つでも混ざるとシナリオ実行そのものが落ちる。
 *   V3 の buildPlanningSetting は既存挙動維持のため null を残しているが、新規に作る
 *   /map の履歴では最初から落として作らない。
 */
export function buildMapPlanningSetting(params: BuildMapPlanningSettingParams): PlanningSettingRequest {
  const {
    vehicleFilterMode, allDeliverySelected, selectedCycleIndexes,
    deliveries, trucks, drivers, scenarioFilter, planningConditions,
    rotations, constraintViolationOnly,
    selectedTruckGroups = [],
  } = params;

  const deliveryById = new Map(deliveries.map((it) => [it.id, it]));
  const licensePlateByTruckId = new Map(trucks.map((it) => [it.id, it.licensePlateValue]));
  const driverNameByDriverId = new Map(drivers.map((it) => [it.id, it.name]));

  // selectedCycleIndexes と selectedDeliveries は API が deliveryId で突き合わせる
  // (planning_scenario_item.rb#selected_deliveries_with_cycle_indexes)。
  // 片方だけ落ちると回転が [1] へ握り潰されるので、同じ絞り込み結果から両方を作る。
  const resolved = selectedCycleIndexes.flatMap((cycle) => {
    const delivery = deliveryById.get(cycle.deliveryId);
    if (!delivery || delivery.truckId == null) return [];
    return [{ cycle, delivery }];
  });

  // グループで絞っていなければ ID 0 の合成エントリを送る。API は selectedGroupIds に 0 が
  // あれば絞り込まない（select_shift_by_gruop_ids）ので、絞っているときに 0 を送ると
  // 再実行のときだけ対象が全車両に広がる
  const groups = selectedTruckGroups.length > 0
    ? selectedTruckGroups.map((it) => ({ id: it.id, name: it.name, truckIds: [...it.truckIds] }))
    : [{
      id: ALL_TRUCKS_GROUP_ID,
      name: ALL_TRUCKS_GROUP_NAME,
      truckIds: [...new Set(trucks.map((it) => it.id))],
    }];

  const deliveryConditions = {
    truckDisplayStatus: toPlanningSettingTruckDisplayStatus(vehicleFilterMode),
    selectedGroupIds: groups.map((it) => it.id),
    selectedGroups: groups,
    allDeliverySelected: resolveAllDeliverySelected({
      allSelected: allDeliverySelected, vehicleFilterMode, rotations, constraintViolationOnly,
    }),
    selectedCycleIndexes: resolved.map(({ cycle }) => cycle),
    selectedDeliveries: resolved.map(({ delivery }) => ({
      deliveryId: delivery.id,
      // 車番・氏名はマスタ未取得だと引けないが、ID は delivery 自身が持っている。
      // 再実行の突き合わせに使うのは ID なので、表示名だけ null にして ID は落とさない
      truckId: delivery.truckId,
      licensePlateValue: licensePlateByTruckId.get(delivery.truckId) ?? null,
      driverId: delivery.driverId,
      driverName: delivery.driverId == null ? null : driverNameByDriverId.get(delivery.driverId) ?? null,
    })),
  };

  const orderConditions = {
    // /map の絞り込みは scenarioFilter が正本。legacy 経路（match_keyword? /
    // match_condition?）は列も演算子も別体系なので、値を写すと別の絞り込みになる
    orderSearchKw: '',
    orderSearchConditions: [],
    scenarioFilter,
  };

  return { deliveryConditions, orderConditions, planningConditions };
}
